import { useState } from "react";
import { format } from "date-fns";
import { Copy, X, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { WhatsAppIcon } from "@/utils/Icons";
import { toDialableDigits } from "@/constants/countryCodes";
import { buildPaymentLinkMessage, openWhatsappChat } from "../utils/paymentLinkShare";
import type { PaymentLinkLead } from "./SendPaymentLinkForm";
import {
  useCancelPaymentLink,
  useListPaymentLinks,
  useSyncPaymentLink,
  type RazorpayPaymentLink,
} from "../api/useRazorpayMutations";

function statusBadge(status: RazorpayPaymentLink["status"]) {
  switch (status) {
    case "paid":
      return { label: "Paid", bg: "#dcfce7", text: "#166534" };
    case "expired":
      return { label: "Expired", bg: "#e5e7eb", text: "#52525b" };
    case "cancelled":
      return { label: "Cancelled", bg: "#fee2e2", text: "#991b1b" };
    case "created":
    default:
      return { label: "Pending", bg: "#fef3c7", text: "#92400e" };
  }
}

export default function PaymentLinksList({
  childId,
  lead,
}: {
  childId: number;
  lead?: PaymentLinkLead | null;
}) {
  const { data, isLoading } = useListPaymentLinks(childId);
  const dialDigits = toDialableDigits(lead?.phone, lead?.phoneCountryCode);
  const cancel = useCancelPaymentLink();
  const sync = useSyncPaymentLink();
  const [cancelingId, setCancelingId] = useState<number | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);

  const links = data?.data ?? [];
  if (!isLoading && links.length === 0) return null;

  return (
    <div className="px-4 pb-4 border-t border-[#e6e8e7]">
      <p className="text-[11px] font-semibold text-[#8a918d] uppercase tracking-[0.5px] pt-3 mb-2">
        Payment Links
      </p>
      {isLoading ? (
        <p className="text-[12px] text-[#8a918d]">Loading…</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {links.map((link) => {
            const badge = statusBadge(link.status);
            const rupees = Math.round(link.amount / 100).toLocaleString("en-IN");
            const created = (() => {
              try {
                return format(new Date(link.createdAt), "d MMM, h:mm a");
              } catch {
                return "";
              }
            })();
            return (
              <li
                key={link.id}
                className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#eef0ef] bg-[#fafbfa]"
              >
                <span
                  className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                  style={{ background: badge.bg, color: badge.text }}
                >
                  {badge.label}
                </span>
                <span className="text-[13px] font-medium text-[#1e302a]">
                  ₹{rupees}
                </span>
                <span className="text-[11px] text-[#8a918d]">
                  {created}
                  {link.installmentSeq ? ` · instalment #${link.installmentSeq}` : ""}
                </span>
                <div className="flex-1" />
                {link.status === "created" && (
                  <>
                    {/* Paid but not showing yet? Ask Razorpay now instead of
                        waiting for the webhook / the 5-minute check. */}
                    <button
                      onClick={() => {
                        setSyncingId(link.id);
                        sync.mutate(link.id, { onSettled: () => setSyncingId(null) });
                      }}
                      className="size-7 flex items-center justify-center rounded-md text-[#697571] hover:text-[#1e302a] hover:bg-[#f0f1f0]"
                      title="Check with Razorpay now"
                      disabled={sync.isPending && syncingId === link.id}
                    >
                      {sync.isPending && syncingId === link.id ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="size-3.5" />
                      )}
                    </button>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(link.rzpShortUrl);
                        toast.success("Link copied");
                      }}
                      className="size-7 flex items-center justify-center rounded-md text-[#697571] hover:text-[#1e302a] hover:bg-[#f0f1f0]"
                      title="Copy link"
                    >
                      <Copy className="size-3.5" />
                    </button>
                    {dialDigits && (
                      <button
                        onClick={() =>
                          openWhatsappChat(
                            dialDigits,
                            buildPaymentLinkMessage({
                              name: lead?.name,
                              amount: link.amount / 100,
                              url: link.rzpShortUrl,
                              expiresAt: link.expiresAt,
                            }),
                          )
                        }
                        className="size-7 flex items-center justify-center rounded-md text-[#697571] hover:text-[#128c4b] hover:bg-[#e9f8ef]"
                        title="Send from my WhatsApp"
                      >
                        <WhatsAppIcon className="size-3.5" />
                      </button>
                    )}
                    <a
                      href={link.rzpShortUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="size-7 flex items-center justify-center rounded-md text-[#697571] hover:text-[#1e302a] hover:bg-[#f0f1f0]"
                      title="Open link"
                    >
                      <ExternalLink className="size-3.5" />
                    </a>
                    <button
                      onClick={() => {
                        setCancelingId(link.id);
                        cancel.mutate(link.id, {
                          onSettled: () => setCancelingId(null),
                        });
                      }}
                      className="size-7 flex items-center justify-center rounded-md text-[#697571] hover:text-[#991b1b] hover:bg-[#fee2e2]"
                      title="Cancel link"
                      disabled={cancel.isPending && cancelingId === link.id}
                    >
                      {cancel.isPending && cancelingId === link.id ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <X className="size-3.5" />
                      )}
                    </button>
                  </>
                )}
                {link.status === "paid" && link.rzpPaymentId && (
                  <span className="text-[11px] text-[#697571]">
                    txn: {link.rzpPaymentId.slice(0, 14)}…
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
